import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { useReactFlow, useViewport } from "@xyflow/react";
import {
  AlignCenterVertical,
  ChevronLeft,
  Focus,
  Grid2x2,
  Grip,
  Hand,
  Minus,
  MousePointer2,
  Plus,
  Slash,
} from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  CANVAS_CURSOR_TOOLS,
  CANVAS_GRID_MODES,
  type CanvasCursorTool,
  type CanvasGridMode,
} from "../canvas-controls.types";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useCanvasChromeEnterAnimation } from "../hooks/useCanvasChromeEnterAnimation";
import {
  canStepZoomIn,
  canStepZoomOut,
  stepZoomPercent,
  zoomLevelToPercent,
} from "../utils/canvasZoomUtils";
import {
  CanvasToolbarButton,
  CanvasToolbarDisplay,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  CanvasToolbarShell,
  CanvasToolbarSubmenu,
} from "./toolbar";

type OpenMenu = "cursor" | "grid" | "zoom" | null;

const GRID_ICONS: Record<CanvasGridMode, typeof Grid2x2> = {
  lines: Grid2x2,
  dots: Grip,
  none: Slash,
};

const CURSOR_ICONS: Record<CanvasCursorTool, typeof MousePointer2> = {
  arrow: MousePointer2,
  hand: Hand,
};

export function CanvasControlsToolbar() {
  const {
    gridMode,
    setGridMode,
    cursorTool,
    setCursorTool,
    alignmentGuides,
    setAlignmentGuides,
  } = useCanvasUi();
  const { zoom } = useViewport();
  const reactFlow = useReactFlow();
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);

  const zoomPercent = zoomLevelToPercent(zoom);
  const shouldAnimateChromeEnter = useCanvasChromeEnterAnimation();

  // Rest as a compact handle (chevron + Recenter); the controls reveal on
  // hover/focus (or while a submenu is open) and re-collapse on leave.
  const [hovered, setHovered] = useState(false);
  const expanded = hovered || openMenu !== null;

  // Animate max-width to the MEASURED content width, not an oversized cap:
  // with a cap, the visible expand finishes in the fast first stretch of the
  // ease curve (abrupt) while the collapse plays out in its gentle tail —
  // 1:1 mapping keeps both directions on the full curve, like the left rail.
  const revealRef = useRef<HTMLDivElement>(null);
  const [revealWidth, setRevealWidth] = useState(0);
  useLayoutEffect(() => {
    // scrollWidth reads the full content width even while max-width clips it
    // (children are shrink-0). Re-measure when the zoom readout changes.
    const el = revealRef.current;
    if (el) setRevealWidth(el.scrollWidth);
  }, [zoomPercent]);

  const setZoomPercent = useCallback(
    (percent: number) => {
      const { x, y } = reactFlow.getViewport();
      void reactFlow.setViewport(
        { x, y, zoom: percent / 100 },
        { duration: 200 },
      );
    },
    [reactFlow],
  );

  const handleRecenter = useCallback(() => {
    void reactFlow.fitView({ duration: 300 });
  }, [reactFlow]);

  const GridIcon = GRID_ICONS[gridMode];
  const CursorIcon = CURSOR_ICONS[cursorTool];

  return (
    <motion.div
      initial={shouldAnimateChromeEnter ? { opacity: 0, y: -12 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
      className="flex items-center"
      data-canvas-control
    >
      <TooltipProvider>
        {/* Expand fires ONLY from the chevron handle (hover or click) —
            Recenter stays a plain one-click action. Collapse waits for the
            pointer to leave the whole bar, so the reveal that grows under
            the cursor doesn't immediately re-collapse. */}
        <div
          className="flex items-center"
          onMouseLeave={() => setHovered(false)}
          onFocus={(e) => {
            // Keyboard focus expands the bar; Radix's programmatic focus
            // restore after a submenu closes (not :focus-visible) must not
            // pin it open once the pointer leaves.
            if ((e.target as HTMLElement).matches(":focus-visible")) {
              setHovered(true);
            }
          }}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setHovered(false);
            }
          }}
        >
          <CanvasToolbarShell
            orientation="horizontal"
            aria-label="Canvas controls"
          >
            {/* Handle — a slim half-width chevron at the outer (left) edge,
                purely the open trigger: hovering (or clicking, for
                keyboard/touch) expands the controls; once open it clips away
                entirely, since collapse waits for the cursor to leave the
                whole bar. Leftmost so its collapse rides the bar's moving
                edge — the reveal grows from Recenter's edge and the anchor
                side never shifts. Rest max-width equals its true 16px width
                so the clip tracks the motion linearly. */}
            <span
              className={cn(
                "flex overflow-hidden transition-[max-width,opacity] duration-300 ease-out motion-reduce:transition-none",
                expanded ? "max-w-0 opacity-0" : "max-w-4 opacity-100",
              )}
              aria-hidden={expanded}
            >
              <CanvasToolbarButton
                className="w-4 min-w-4 px-0"
                aria-label="Show canvas controls"
                aria-expanded={expanded}
                tabIndex={expanded ? -1 : 0}
                onMouseEnter={() => setHovered(true)}
                onClick={() => setHovered(true)}
              >
                <ChevronLeft className="size-3.5 shrink-0" strokeWidth={2} />
              </CanvasToolbarButton>
            </span>

            {/* Controls reveal leftward from the Recenter button's edge (the
                dock is right-anchored). max-width animates the reveal;
                overflow clips the collapse. */}
            <div
              ref={revealRef}
              className={cn(
                "flex items-center overflow-hidden transition-[max-width,opacity] duration-300 ease-out motion-reduce:transition-none",
                expanded ? "opacity-100" : "pointer-events-none opacity-0",
              )}
              style={{
                maxWidth: expanded && revealWidth ? `${revealWidth}px` : "0px",
              }}
              aria-hidden={!expanded}
            >
              <CanvasToolbarGroup>
                <CanvasToolbarSubmenu
                  open={openMenu === "cursor"}
                  onOpenChange={(open) => setOpenMenu(open ? "cursor" : null)}
                  side="bottom"
                  trigger={
                    <CanvasToolbarButton
                      label="Cursor"
                      dropdown
                      aria-expanded={openMenu === "cursor"}
                    >
                      <CursorIcon className="size-4" strokeWidth={2} />
                    </CanvasToolbarButton>
                  }
                >
                  <CanvasToolbarGroup>
                    {CANVAS_CURSOR_TOOLS.map((tool) => {
                      const Icon = CURSOR_ICONS[tool.key];
                      return (
                        <CanvasToolbarButton
                          key={tool.key}
                          label={tool.name}
                          active={cursorTool === tool.key}
                          onClick={() => {
                            setCursorTool(tool.key);
                            setOpenMenu(null);
                          }}
                        >
                          <Icon className="size-4" strokeWidth={2} />
                        </CanvasToolbarButton>
                      );
                    })}
                  </CanvasToolbarGroup>
                </CanvasToolbarSubmenu>

                <CanvasToolbarSubmenu
                  open={openMenu === "grid"}
                  onOpenChange={(open) => setOpenMenu(open ? "grid" : null)}
                  side="bottom"
                  trigger={
                    <CanvasToolbarButton
                      label="Grid"
                      dropdown
                      aria-expanded={openMenu === "grid"}
                    >
                      <GridIcon className="size-4" strokeWidth={2} />
                    </CanvasToolbarButton>
                  }
                >
                  <CanvasToolbarGroup>
                    {CANVAS_GRID_MODES.map((mode) => {
                      const Icon = GRID_ICONS[mode.key];
                      return (
                        <CanvasToolbarButton
                          key={mode.key}
                          label={mode.name}
                          active={gridMode === mode.key}
                          onClick={() => {
                            setGridMode(mode.key);
                            setOpenMenu(null);
                          }}
                        >
                          <Icon className="size-4" strokeWidth={2} />
                        </CanvasToolbarButton>
                      );
                    })}
                  </CanvasToolbarGroup>
                </CanvasToolbarSubmenu>

                <CanvasToolbarButton
                  label={
                    alignmentGuides
                      ? "Alignment guides on"
                      : "Alignment guides off"
                  }
                  active={alignmentGuides}
                  onClick={() => setAlignmentGuides((prev) => !prev)}
                >
                  <AlignCenterVertical className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
              </CanvasToolbarGroup>

              <CanvasToolbarDivider />

              <CanvasToolbarGroup>
                <CanvasToolbarSubmenu
                  open={openMenu === "zoom"}
                  onOpenChange={(open) => setOpenMenu(open ? "zoom" : null)}
                  side="bottom"
                  trigger={
                    <CanvasToolbarButton
                      label="Zoom"
                      dropdown
                      monoLabel={`${zoomPercent}%`}
                      aria-expanded={openMenu === "zoom"}
                    />
                  }
                >
                  <CanvasToolbarGroup>
                    <CanvasToolbarButton
                      label="Zoom out"
                      disabled={!canStepZoomOut(zoomPercent)}
                      onClick={() =>
                        setZoomPercent(stepZoomPercent(zoomPercent, -1))
                      }
                    >
                      <Minus className="size-4" strokeWidth={2} />
                    </CanvasToolbarButton>
                    <CanvasToolbarDisplay>{zoomPercent}%</CanvasToolbarDisplay>
                    <CanvasToolbarButton
                      label="Zoom in"
                      disabled={!canStepZoomIn(zoomPercent)}
                      onClick={() =>
                        setZoomPercent(stepZoomPercent(zoomPercent, 1))
                      }
                    >
                      <Plus className="size-4" strokeWidth={2} />
                    </CanvasToolbarButton>
                  </CanvasToolbarGroup>
                </CanvasToolbarSubmenu>
              </CanvasToolbarGroup>

              <CanvasToolbarDivider />
            </div>

            {/* Recenter — the exposed rest-state face of the bar: always
                visible, one click re-fits the view without ever expanding. */}
            <CanvasToolbarGroup>
              <CanvasToolbarButton label="Recenter" onClick={handleRecenter}>
                <Focus className="size-4" strokeWidth={2} />
              </CanvasToolbarButton>
            </CanvasToolbarGroup>
          </CanvasToolbarShell>
        </div>
      </TooltipProvider>
    </motion.div>
  );
}
